/**
 * Copyright (c) SAGE3 Development Team 2023. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 */

import { useState } from 'react';
import {
  Box,
  Text,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Button,
  useDisclosure,
  Portal,
} from '@chakra-ui/react';

import { useUserSettings, useAppStore } from '@sage3/frontend';

type WindowTitleProps = {
  size: { width: number; height: number };
  scale: number;
  title: string;
  selected: boolean;
  appId: string;
};

/**
 * A WindowTitle is a box that is displayed over the top of the window when
 * AppTitles are enabled.
 */
export function WindowTitle(props: WindowTitleProps) {
  const size = props.size;
  const scale = props.scale;
  const title = props.title;
  const selected = props.selected;
  const appId = props.appId;
  const titleBackground = useColorModeValue('#00000000', '#ffffff26');
  const titleBrightness = useColorModeValue('85%', '65%');
  const titleColor = useColorModeValue('white', 'white');

  const { settings } = useUserSettings();
  const appTitles = settings.showAppTitles;
  const update = useAppStore((state) => state.update);

  // Rename modal state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [renameValue, setRenameValue] = useState<string>('');

  const handleDoubleClick = () => {
    setRenameValue(title);
    onOpen();
  };

  const handleRename = () => {
    if (renameValue.trim() && renameValue.trim() !== title) {
      update(appId, { title: renameValue.trim() });
    }
    onClose();
  };

  if (!appTitles && !selected) return null;

  return (
    <>
      <Box
        position="absolute"
        top="0px"
        left="0px"
        width={size.width}
        transform={`translate(-${2 / scale}px, calc(-100% - ${4 / scale}px))`}
        display="flex"
        justifyContent="left"
        alignItems="center"
        pointerEvents="auto"
        cursor="pointer"
      >
        <Text
          color={titleColor}
          fontSize={16 / scale}
          whiteSpace="nowrap"
          textOverflow="ellipsis"
          overflow="hidden"
          background={titleBackground}
          backdropFilter={`blur(${Math.max(5, 5 / scale)}px) brightness(${titleBrightness})`}
          borderRadius={6}
          px={2}
          userSelect={'none'}
          onDoubleClick={handleDoubleClick}
          title="Double-click to rename"
        >
          {title}
        </Text>
      </Box>

      {/* Rename modal - use Portal to render at document root */}
      <Portal>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Rename Application</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Input
                placeholder="Enter new name"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRename();
                  if (e.key === 'Escape') onClose();
                }}
                autoFocus
              />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="green" onClick={handleRename} mr={3}>
                Rename
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Portal>
    </>
  );
}
